import React from "react";
import CheckIcon from "@material-ui/icons/Check";
import * as ErrorMsgConstants from "../../SharedModules/Messages/ErrorMsgConstants";


/** TODO: revert to 6 when security team aligns OTP length */
const OTP_LENGTH = 8;
const SESSION_TIMEOUT_MS = 10 * 60 * 1000;
const RESEND_COOLDOWN_SEC = 30;

const emptyDigits = () => Array(OTP_LENGTH).fill("");

function getGreetingName(username) {
  if (!username?.trim()) return "";
  const value = username.trim();
  if (value.includes(",")) {
    const parts = value.split(",").map(p => p.trim()).filter(Boolean);
    return parts[parts.length - 1] || value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toUpperCase();
}

function OTPPage(props) {
  const [digits, setDigits] = React.useState(emptyDigits);
  const [otpReqErr, setOtpReqErr] = React.useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = React.useState(RESEND_COOLDOWN_SEC);
  const inputRefs = React.useRef([]);

  const greetingName = getGreetingName(props.username);
  const resendCooldownActive = resendSecondsLeft > 0;
  const resendPermanentlyDisabled = props.resendPermanentlyDisabled === true;
  const resendDisabled = resendCooldownActive || resendPermanentlyDisabled;

  const startResendCooldown = React.useCallback(() => {
    setResendSecondsLeft(RESEND_COOLDOWN_SEC);
  }, []);

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = "OTP Page";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  React.useEffect(() => {
    inputRefs.current[0]?.focus();
    startResendCooldown();
  }, [startResendCooldown]);

  React.useEffect(() => {
    if (resendSecondsLeft <= 0) return undefined;
    const timerId = window.setTimeout(() => {
      setResendSecondsLeft(seconds => seconds - 1);
    }, 1000);
    return () => window.clearTimeout(timerId);
  }, [resendSecondsLeft]);

  const sessionTimerRef = React.useRef(null);
  const onSessionExpire = props.onSessionExpire;

  const resetSessionTimer = React.useCallback(() => {
    if (sessionTimerRef.current) {
      window.clearTimeout(sessionTimerRef.current);
    }
    sessionTimerRef.current = window.setTimeout(() => {
      onSessionExpire?.();
    }, SESSION_TIMEOUT_MS);
  }, [onSessionExpire]);

  React.useEffect(() => {
    resetSessionTimer();
    return () => {
      if (sessionTimerRef.current) {
        window.clearTimeout(sessionTimerRef.current);
      }
    };
  }, [resetSessionTimer]);

  const focusInput = index => {
    if (index >= 0 && index < OTP_LENGTH) {
      inputRefs.current[index]?.focus();
    }
  };

  const updateDigit = (index, value) => {
    const sanitized = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = sanitized;
    setDigits(next);
    setOtpReqErr(false);
    if (sanitized && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
    return sanitized;
  };

  const handleChange = (index, event) => {
    resetSessionTimer();
    const value = event.target.value;
    if (value.length > 1) {
      const chars = value.replace(/\D/g, "").slice(0, OTP_LENGTH - index).split("");
      const next = [...digits];
      chars.forEach((char, offset) => {
        if (index + offset < OTP_LENGTH) {
          next[index + offset] = char;
        }
      });
      setDigits(next);
      setOtpReqErr(false);
      focusInput(Math.min(index + chars.length, OTP_LENGTH - 1));
      return;
    }
    updateDigit(index, value);
  };

  const handleKeyDown = (index, event) => {
    resetSessionTimer();
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
    if (event.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    }
    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = event => {
    resetSessionTimer();
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = emptyDigits();
    pasted.split("").forEach((char, i) => {
      next[i] = char;
    });
    setDigits(next);
    setOtpReqErr(false);
    focusInput(Math.min(pasted.length, OTP_LENGTH - 1));
  };

  const handleVerify = () => {
    resetSessionTimer();
    const otp = digits.join("");
    if (otp.length !== OTP_LENGTH) {
      setOtpReqErr(true);
      return;
    }
    setOtpReqErr(false);
    props.verifyOtpAndCompleteLogin(otp);
  };

  const handleResend = () => {
    if (resendPermanentlyDisabled || resendCooldownActive) return;
    resetSessionTimer();
    setDigits(emptyDigits());
    setOtpReqErr(false);
    props.onResendOtp?.();
    startResendCooldown();
    focusInput(0);
  };

  return (
    <div className="otp-verification ligin-form loginForm">
      <h1 className="otp-greeting">
        Hello{greetingName ? ` ${greetingName}` : ""}!
      </h1>

      <div className="otp-card">
        <p className="otp-instruction">
          Enter the OTP sent to your registered email address.
        </p>

        <div
          className="otp-inputs"
          onPaste={handlePaste}
          role="group"
          aria-label="One-time passcode"
        >
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={el => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              className={`otp-digit-input${otpReqErr ? " otp-digit-input--error" : ""}`}
              value={digit}
              data-test={`otp-digit-${index}`}
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              onChange={e => handleChange(index, e)}
              onKeyDown={e => handleKeyDown(index, e)}
            />
          ))}
        </div>

        {otpReqErr ? (
          <p className="otp-error-text" role="alert">
            {ErrorMsgConstants.OTP_REQ_ERR}
          </p>
        ) : null}

        <div className="otp-actions">
          <button
            type="button"
            data-test="resend-otp"
            title={
              resendPermanentlyDisabled
                ? "Resend limit reached"
                : resendCooldownActive
                  ? `Resend available in ${resendSecondsLeft}s`
                  : "Resend OTP"
            }
            className="otp-resend-btn"
            disabled={resendDisabled}
            aria-disabled={resendDisabled}
            onClick={handleResend}
          >
            {resendCooldownActive && !resendPermanentlyDisabled
              ? `RESEND (${resendSecondsLeft})`
              : "RESEND"}
          </button>
          <button
            type="button"
            data-test="verify-otp"
            title="Verify"
            className="otp-verify-btn"
            onClick={handleVerify}
          >
            <CheckIcon className="otp-verify-icon" fontSize="small" />
            VERIFY
          </button>
          
        </div>
      </div>

      <p className="otp-security-note">
        This page will return to login in 10 minutes. For your security, after 3 failed login attempts account will be locked.
      </p>
    </div>
  );
}

export default OTPPage;
